import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import teamService from '@/services/teamService.ts';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Props {
  isOpen: boolean;
  teamId?: string;
  currentName?: string;
  onClose: () => void;
  onSaved: () => void;
}

export default function CreateEditTeamModal({ isOpen, teamId, currentName, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = !!teamId;

  useEffect(() => {
    if (isOpen) {
      setName(currentName ?? '');
      setError('');
    }
  }, [isOpen, currentName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) { setError(t('createEditTeam.nameRequired')); return; }
    if (trimmed.length > 200) { setError(t('createEditTeam.nameMaxLength')); return; }

    setError('');
    setIsSaving(true);
    try {
      if (isEditing) {
        await teamService.updateTeam(teamId, { name: trimmed });
      } else {
        await teamService.createTeam({ name: trimmed });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('createEditTeam.saveError'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{isEditing ? t('createEditTeam.editTitle') : t('createEditTeam.createTitle')}</DialogTitle>
          <DialogDescription>
            {isEditing ? t('createEditTeam.editSubtitle') : t('createEditTeam.createSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <label htmlFor="teamName" className="mb-1.5 block text-sm font-medium text-foreground">
            {t('createEditTeam.nameLabel')}
          </label>
          <input
            id="teamName"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('createEditTeam.namePlaceholder')}
            className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
            autoFocus
          />
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}

          <DialogFooter className="mt-5">
            <Button type="button" variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? t('common.saving') : isEditing ? t('createEditTeam.editSubmit') : t('createEditTeam.createSubmit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
